import { useState, useEffect } from 'react';
import { Settings, Save, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../../utils/api';

export default function SystemSettingsTab() {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  
  const [settings, setSettings] = useState({
    client_free_use_count: '',
    uninstall_retention_token_rmb: '',
    uninstall_retention_cash_rmb: '',
    uninstall_retention_cash_lock_hours: '',
    uninstall_retention_required_consume_rmb: '',
    uninstall_survey_reasons: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/settings');
      if (response.data.success) {
        // 由于 api.js 拦截器会将对象类型的 data 属性打平合并到 response.data 中，
        // 所以实际的数据直接挂在 response.data 下，而不是 response.data.data
        const data = response.data;
        setSettings(prev => ({
          ...prev,
          client_free_use_count: (typeof data.client_free_use_count === 'object' ? data.client_free_use_count?.value : data.client_free_use_count) || '',
          uninstall_retention_token_rmb: (typeof data.uninstall_retention_token_rmb === 'object' ? data.uninstall_retention_token_rmb?.value : data.uninstall_retention_token_rmb) || '',
          uninstall_retention_cash_rmb: (typeof data.uninstall_retention_cash_rmb === 'object' ? data.uninstall_retention_cash_rmb?.value : data.uninstall_retention_cash_rmb) || '',
          uninstall_retention_cash_lock_hours: (typeof data.uninstall_retention_cash_lock_hours === 'object' ? data.uninstall_retention_cash_lock_hours?.value : data.uninstall_retention_cash_lock_hours) || '',
          uninstall_retention_required_consume_rmb: (typeof data.uninstall_retention_required_consume_rmb === 'object' ? data.uninstall_retention_required_consume_rmb?.value : data.uninstall_retention_required_consume_rmb) || '',
          uninstall_survey_reasons: (typeof data.uninstall_survey_reasons === 'object' ? data.uninstall_survey_reasons?.value : data.uninstall_survey_reasons) || ''
        }));
      }
    } catch (err) {
      console.error('获取配置失败:', err);
    }
  };

  const handleSettingChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveSettings = async () => {
    const freeCountText = String(settings.client_free_use_count ?? '').trim();
    if (!/^\d+$/.test(freeCountText)) {
      setError('「免费使用次数」需填写非负整数');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMsg('');
    
    try {
      const response = await api.post('/admin/settings', { settings });
      if (response.data.success) {
        setSuccessMsg('系统配置更新成功');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || '配置更新失败');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-4 rounded-xl flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 p-4 rounded-xl flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          <p>{successMsg}</p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">系统设置</h2>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors text-sm font-medium"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            保存配置
          </button>
        </div>
        <div className="p-6 space-y-8">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">🆓</span>
              客户端免费次数设置
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              非会员客户端累计可免费使用的次数，用完后引导开通会员；有效会员不限次数、不扣减。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">免费使用次数（次）</label>
                  <div className="relative">
                    <input type="number" min="0" step="1" name="client_free_use_count" value={settings.client_free_use_count} onChange={handleSettingChange} className="w-full px-4 py-2 pr-10 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">次</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">调整后立即生效；调大次数时，已使用过的老用户剩余次数同步增加（默认 5）。</p>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400">📦</span>
              客户端卸载挽留设置
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              客户端用户点击卸载时弹窗挽留：领取福利立即下发 token 并开始现金红包倒计时，倒计时结束后需消耗达标才能提现。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">挽留 Token 金额（元）</label>
                  <div className="relative">
                    <input type="number" min="0" name="uninstall_retention_token_rmb" value={settings.uninstall_retention_token_rmb} onChange={handleSettingChange} className="w-full px-4 py-2 pr-10 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">元</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">领取福利时立即下发的 5 元 token（默认 5）。</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">现金红包金额（元）</label>
                  <div className="relative">
                    <input type="number" min="0" name="uninstall_retention_cash_rmb" value={settings.uninstall_retention_cash_rmb} onChange={handleSettingChange} className="w-full px-4 py-2 pr-10 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">元</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">锁定的现金红包金额，提现固定金额（默认 3）。</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">现金红包锁定时长（小时）</label>
                  <div className="relative">
                    <input type="number" min="1" name="uninstall_retention_cash_lock_hours" value={settings.uninstall_retention_cash_lock_hours} onChange={handleSettingChange} className="w-full px-4 py-2 pr-10 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">小时</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">领取福利后开始倒计时，倒计时结束后才能提现（默认 72）。</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">提现需消耗 Token 达标线（元）</label>
                  <div className="relative">
                    <input type="number" min="0" name="uninstall_retention_required_consume_rmb" value={settings.uninstall_retention_required_consume_rmb} onChange={handleSettingChange} className="w-full px-4 py-2 pr-10 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">元</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">领取以来真实消耗 token（按用户倍率折算）达到此金额才可提现（默认 3）。</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">卸载原因选项（JSON 数组）</label>
                  <textarea
                    name="uninstall_survey_reasons"
                    value={settings.uninstall_survey_reasons}
                    onChange={handleSettingChange}
                    rows={4}
                    placeholder='["卡顿","功能少","太忙了","其他"]'
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">卸载原因收集页的多选选项，需为合法 JSON 数组格式。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
